/**
 * UrgencyBadge — canonical urgency primitive (Design Contract addendum, 2026-04-28).
 *
 * Visual urgency hierarchy across the platform must be CONSISTENT, not noisy:
 *   • critical  — red,    pulsing dot   — SLA missed, wallet blocking send, contract breach
 *   • warning   — amber,  static dot    — act-within-48h, wallet short for planned wave, draft >24h
 *   • live      — teal,   pulsing dot   — live wave, on-pace KPIs, healthy data
 *   • neutral   — gray,   no dot        — non-urgent metadata
 *
 * Use this everywhere you'd otherwise hand-roll a colored pill. Same DSL,
 * same visual weight, same rules.
 *
 *   <UrgencyBadge level="warning" label="Act within 48h" />
 *   <UrgencyBadge level="critical" label="Wallet short" context="$3.2K shortfall" />
 *
 * For page-level critical signals (wallet blocking, wave paused, OAuth expired)
 * use <UrgencyBanner /> instead — it spans the top of the page, not a chip.
 */
import React from 'react';
import { AlertTriangle, AlertCircle, Activity } from 'lucide-react';

const PALETTE = {
  critical: {
    bg: 'rgba(239, 68, 68, 0.12)',
    border: 'rgba(239, 68, 68, 0.35)',
    fg: '#EF4444',
    dot: '#EF4444',
    pulse: true,
    icon: AlertCircle,
  },
  warning: {
    bg: 'rgba(245, 158, 11, 0.12)',
    border: 'rgba(245, 158, 11, 0.35)',
    fg: '#F59E0B',
    dot: '#F59E0B',
    pulse: false,
    icon: AlertTriangle,
  },
  live: {
    bg: 'rgba(0, 212, 255, 0.10)',
    border: 'rgba(0, 212, 255, 0.30)',
    fg: '#00D4FF',
    dot: '#00D4FF',
    pulse: true,
    icon: Activity,
  },
  neutral: {
    bg: 'rgba(161, 161, 170, 0.10)',
    border: 'rgba(161, 161, 170, 0.25)',
    fg: '#a1a1aa',
    dot: '#a1a1aa',
    pulse: false,
    icon: null,
  },
};

export function UrgencyBadge({ level = 'neutral', label, context, icon, className = '', testId, ...rest }) {
  const palette = PALETTE[level] || PALETTE.neutral;
  const Icon = icon === false ? null : (icon || palette.icon);
  return (
    <span
      data-testid={testId || `urgency-badge-${level}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${className}`}
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.fg,
      }}
      {...rest}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${palette.pulse ? 'animate-pulse' : ''}`}
        style={{ background: palette.dot }}
      />
      {Icon && <Icon className="h-2.5 w-2.5" />}
      <span>{label}</span>
      {context && <span className="opacity-70 normal-case font-semibold ml-0.5">· {context}</span>}
    </span>
  );
}

/**
 * UrgencyBanner — full-width page-level urgent signal. Use sparingly: this
 * is for top-of-page situations the user must address before they can act
 * (wallet blocking a send, wave paused, OAuth re-auth required).
 */
export function UrgencyBanner({ level = 'warning', title, body, action, testId, className = '' }) {
  const palette = PALETTE[level] || PALETTE.warning;
  const Icon = palette.icon || AlertTriangle;
  return (
    <div
      data-testid={testId || `urgency-banner-${level}`}
      className={`rounded-xl p-4 flex items-start gap-3 ${className}`}
      style={{ background: palette.bg, border: `1px solid ${palette.border}` }}
    >
      <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: palette.fg }} />
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-black" style={{ color: palette.fg }}>{title}</p>
        {body && <p className="text-[11px] text-gray-300 mt-0.5 leading-relaxed">{body}</p>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

export default UrgencyBadge;
